import Component from "@glimmer/component";
import { schedule } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { modifier } from "ember-modifier";
import domFromString from "discourse/lib/dom-from-string";
import applyLightbox from "discourse/lib/lightbox";
import { escapeExpression } from "discourse/lib/utilities";
import { and } from "discourse/truth-helpers";
import DDecoratedHtml from "discourse/ui-kit/d-decorated-html";
import { i18n } from "discourse-i18n";
import ChatUpload from "discourse/plugins/chat/discourse/components/chat-upload";
import Collapser from "discourse/plugins/chat/discourse/components/collapser";
import LazyVideo from "discourse/plugins/discourse-lazy-videos/discourse/components/lazy-video" with {
  discourseImport: "optional",
};
import getVideoAttributes from "discourse/plugins/discourse-lazy-videos/lib/lazy-video-attributes" with {
  discourseImport: "optional",
};

export default class ChatMessageCollapser extends Component {
  @service siteSettings;

  lightbox = modifier((element) => {
    let cancelled = false;

    schedule("afterRender", () => {
      if (cancelled || this.args.uploads.length === 0 || !element.isConnected) {
        return;
      }

      if (element.querySelector(".lightbox")) {
        applyLightbox(element);
      }
    });

    return () => {
      cancelled = true;
    };
  });

  get hasUploads() {
    return hasUploads(this.args.uploads);
  }

  get uploadsHeader() {
    let name;
    if (this.args.uploads.length === 1) {
      const upload = this.args.uploads[0];
      name =
        upload.optimized_video?.original_filename || upload.original_filename;
    } else {
      name = i18n("chat.uploaded_files", { count: this.args.uploads.length });
    }
    return trustHTML(
      `<span class="chat-message-collapser-link-small">${escapeExpression(
        name
      )}</span>`
    );
  }

  get uploadsRenderContext() {
    return [
      {
        signature: uploadSignature(this.args.uploads),
        uploads: this.args.uploads,
      },
    ];
  }

  get cookedBodies() {
    const elements = Array.prototype.slice.call(
      domFromString(this.args.cooked)
    );

    if (hasLazyVideo(elements)) {
      return this.lazyVideoCooked(elements);
    }

    if (hasImageOnebox(elements)) {
      return this.imageOneboxCooked(elements);
    }

    if (hasImage(elements)) {
      return this.imageCooked(elements);
    }

    if (hasGallery(elements)) {
      return this.galleryCooked(elements);
    }

    return [];
  }

  lazyVideoCooked(elements) {
    return elements.reduce((acc, e) => {
      if (this.siteSettings.lazy_videos_enabled && lazyVideoPredicate(e)) {
        const videoAttributes = getVideoAttributes(e);

        if (this.siteSettings[`lazy_${videoAttributes.providerName}_enabled`]) {
          const link = escapeExpression(videoAttributes.url);
          const title = escapeExpression(videoAttributes.title);
          const header = trustHTML(
            `<a target="_blank" class="chat-message-collapser-link" rel="noopener noreferrer" href="${link}">${title}</a>`
          );

          acc.push({
            header,
            body: e.outerHTML,
            videoAttributes,
            needsCollapser: true,
          });
        } else {
          acc.push({ body: e.outerHTML, needsCollapser: false });
        }
      } else {
        acc.push({ body: e.outerHTML, needsCollapser: false });
      }
      return acc;
    }, []);
  }

  imageOneboxCooked(elements) {
    return elements.reduce((acc, e) => {
      if (imageOneboxPredicate(e)) {
        let link = animatedImagePredicate(e)
          ? e.firstChild.src
          : e.firstElementChild.href;

        link = escapeExpression(link);
        const header = trustHTML(
          `<a target="_blank" class="chat-message-collapser-link-small" rel="noopener noreferrer" href="${link}">${link}</a>`
        );
        acc.push({ header, body: e.outerHTML, needsCollapser: true });
      } else {
        acc.push({ body: e.outerHTML, needsCollapser: false });
      }
      return acc;
    }, []);
  }

  imageCooked(elements) {
    return elements.reduce((acc, e) => {
      if (imagePredicate(e)) {
        const link = escapeExpression(e.firstElementChild.src);
        const alt = escapeExpression(e.firstElementChild.alt);
        const header = trustHTML(
          `<a target="_blank" class="chat-message-collapser-link-small" rel="noopener noreferrer" href="${link}">${
            alt || link
          }</a>`
        );
        acc.push({ header, body: e.outerHTML, needsCollapser: true });
      } else {
        acc.push({ body: e.outerHTML, needsCollapser: false });
      }
      return acc;
    }, []);
  }

  galleryCooked(elements) {
    return elements.reduce((acc, e) => {
      if (galleryPredicate(e)) {
        const link = escapeExpression(e.firstElementChild.href);
        const title = escapeExpression(
          e.firstElementChild.firstElementChild.textContent
        );
        e.firstElementChild.removeChild(e.firstElementChild.firstElementChild);
        const header = trustHTML(
          `<a target="_blank" class="chat-message-collapser-link-small" rel="noopener noreferrer" href="${link}">${title}</a>`
        );
        acc.push({ header, body: e.outerHTML, needsCollapser: true });
      } else {
        acc.push({ body: e.outerHTML, needsCollapser: false });
      }
      return acc;
    }, []);
  }

  <template>
    <div class="chat-message-collapser">
      {{#if this.hasUploads}}
        <DDecoratedHtml
          @html={{trustHTML @cooked}}
          @decorate={{@decorate}}
          @className="chat-cooked"
        />

        <Collapser
          @header={{this.uploadsHeader}}
          @onToggle={{@onToggleCollapse}}
        >
          {{#each this.uploadsRenderContext key="signature" as |uploadContext|}}
            <div class="chat-uploads" {{this.lightbox}}>
              {{#each uploadContext.uploads key="@index" as |upload|}}
                <ChatUpload @upload={{upload}} />
              {{/each}}
            </div>
          {{/each}}
        </Collapser>
      {{else}}
        {{#each this.cookedBodies as |cooked|}}
          {{#if cooked.needsCollapser}}
            <Collapser
              @header={{cooked.header}}
              @onToggle={{@onToggleCollapse}}
            >
              {{#if (and cooked.videoAttributes LazyVideo)}}
                <div class="chat-message-collapser-lazy-video">
                  <LazyVideo @videoAttributes={{cooked.videoAttributes}} />
                </div>
              {{else}}
                <DDecoratedHtml
                  @html={{trustHTML cooked.body}}
                  @decorate={{@decorate}}
                  @className="chat-cooked"
                />
              {{/if}}
            </Collapser>
          {{else}}
            <DDecoratedHtml
              @html={{trustHTML cooked.body}}
              @decorate={{@decorate}}
              @className="chat-cooked"
            />
          {{/if}}
        {{/each}}
      {{/if}}
    </div>
  </template>
}

function lazyVideoPredicate(e) {
  return e.classList.contains("lazy-video-container");
}

function hasLazyVideo(elements) {
  return elements.some((e) => lazyVideoPredicate(e));
}

function animatedImagePredicate(e) {
  return (
    e.firstChild &&
    e.firstChild.nodeName === "IMG" &&
    e.firstChild.classList.contains("animated") &&
    e.firstChild.classList.contains("onebox")
  );
}

function externalImageOnebox(e) {
  return (
    e.firstElementChild &&
    e.firstElementChild.nodeName === "A" &&
    e.firstElementChild.classList.contains("onebox") &&
    e.firstElementChild.firstElementChild &&
    e.firstElementChild.firstElementChild.nodeName === "IMG"
  );
}

function imageOneboxPredicate(e) {
  return animatedImagePredicate(e) || externalImageOnebox(e);
}

function hasImageOnebox(elements) {
  return elements.some((e) => imageOneboxPredicate(e));
}

function hasUploads(uploads) {
  return uploads?.length > 0;
}

function uploadSignature(uploads = []) {
  return uploads
    .map((upload) => {
      return [
        upload.id,
        upload.url,
        upload.thumbnail?.url,
        upload.short_path,
        upload.width,
        upload.height,
      ].join(":");
    })
    .join("|");
}

function imagePredicate(e) {
  return (
    e.nodeName === "P" &&
    e.firstElementChild &&
    e.firstElementChild.nodeName === "IMG" &&
    !e.firstElementChild.classList.contains("emoji")
  );
}

function hasImage(elements) {
  return elements.some((e) => imagePredicate(e));
}

function galleryPredicate(e) {
  return (
    e.firstElementChild &&
    e.firstElementChild.nodeName === "A" &&
    e.firstElementChild.firstElementChild &&
    e.firstElementChild.firstElementChild.classList.contains("outer-box")
  );
}

function hasGallery(elements) {
  return elements.some((e) => galleryPredicate(e));
}

export function isCollapsible(cooked, uploads) {
  const elements = Array.prototype.slice.call(domFromString(cooked));

  return (
    hasLazyVideo(elements) ||
    hasImageOnebox(elements) ||
    hasUploads(uploads) ||
    hasImage(elements) ||
    hasGallery(elements)
  );
}
